import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Plus, Trash2 } from 'lucide-react'
import { api, errorMessage } from '@/lib/api'
import Card, { SectionLabel } from '@/components/ui/Card'
import Button from '@/components/ui/Button'
import { Field, Input } from '@/components/ui/Field'
import { useToast } from '@/components/ui/toast'

type Zone = 'west' | 'east'

export interface RateCardData {
  id: number
  couriers: string[]
  rates: { zone: Zone; min_kg: number; max_kg: number | null; price: number }[]
}

interface RateRow {
  /** Kilograms as typed; max left empty means "and above". */
  min_kg: string
  max_kg: string
  price: string
}

interface CardDraft {
  /** Comma separated while editing, e.g. "J&T, NinjaVan". */
  couriers: string
  rows: Record<Zone, RateRow[]>
}

const ZONES: { key: Zone; label: string; hint: string }[] = [
  { key: 'west', label: 'West Malaysia', hint: 'Semenanjung' },
  { key: 'east', label: 'East Malaysia', hint: 'Sabah, Sarawak, Labuan' },
]

/**
 * Courier rate cards. A card names the courier(s) it prices - several
 * couriers can share one price table, and couriers that price differently
 * get their own card. Each row is an explicit weight range, the way a
 * courier price list reads: 0 - 1 kg RM7, 1.01 - 5 kg RM12, and so on.
 * Leaving "to" empty makes that row "and above". Checkout only offers
 * couriers that appear on a card. Seeded once from the server; afterwards
 * the admin's edits win.
 */
export default function CourierRatesTab({ initial, onSaved }: { initial: RateCardData[]; onSaved: () => void }) {
  const toast = useToast()
  const [cards, setCards] = useState<CardDraft[]>(() =>
    initial.map((card) => ({
      couriers: card.couriers.join(', '),
      rows: {
        west: card.rates.filter((r) => r.zone === 'west').map(toRow),
        east: card.rates.filter((r) => r.zone === 'east').map(toRow),
      },
    })),
  )

  const saveRates = useMutation({
    mutationFn: async () =>
      (
        await api.put('/admin/delivery/rates', {
          cards: cards.map((card) => ({
            couriers: card.couriers
              .split(',')
              .map((name) => name.trim())
              .filter(Boolean),
            rates: (['west', 'east'] as const).flatMap((zone) =>
              card.rows[zone].map((row) => ({
                zone,
                min_kg: Number(row.min_kg) || 0,
                max_kg: row.max_kg === '' ? null : Number(row.max_kg),
                price: Number(row.price),
              })),
            ),
          })),
        })
      ).data,
    onSuccess: (res) => {
      onSaved()
      toast.success(res.message)
    },
    onError: (err) => toast.error('Save failed', errorMessage(err)),
  })

  const updateCard = (cardIndex: number, patch: Partial<CardDraft>) =>
    setCards((prev) => prev.map((card, i) => (i === cardIndex ? { ...card, ...patch } : card)))

  const updateRow = (cardIndex: number, zone: Zone, index: number, patch: Partial<RateRow>) =>
    setCards((prev) =>
      prev.map((card, i) =>
        i === cardIndex
          ? {
              ...card,
              rows: {
                ...card.rows,
                [zone]: card.rows[zone].map((row, j) => (j === index ? { ...row, ...patch } : row)),
              },
            }
          : card,
      ),
    )

  const addRow = (cardIndex: number, zone: Zone) =>
    setCards((prev) =>
      prev.map((card, i) => {
        if (i !== cardIndex) return card
        // Prefill "from" to continue where the last bounded range ended.
        const lastMax = [...card.rows[zone]].reverse().find((row) => row.max_kg !== '')?.max_kg
        const nextMin = lastMax ? String(Math.round((Number(lastMax) + 0.01) * 100) / 100) : ''
        return {
          ...card,
          rows: { ...card.rows, [zone]: [...card.rows[zone], { min_kg: nextMin, max_kg: '', price: '' }] },
        }
      }),
    )

  const removeRow = (cardIndex: number, zone: Zone, index: number) =>
    setCards((prev) =>
      prev.map((card, i) =>
        i === cardIndex
          ? { ...card, rows: { ...card.rows, [zone]: card.rows[zone].filter((_, j) => j !== index) } }
          : card,
      ),
    )

  const addCard = () =>
    setCards((prev) => [...prev, { couriers: '', rows: { west: [], east: [] } }])

  const removeCard = (cardIndex: number) => setCards((prev) => prev.filter((_, i) => i !== cardIndex))

  return (
    <Card>
      <SectionLabel>Courier rates by weight</SectionLabel>
      <p className="mb-4 text-xs text-muted">
        Charged per order by total weight (each product has a weight per piece, set on the product form). Each card
        prices the courier(s) named on it - put couriers with the same prices on one card, and give a courier its own
        card when it prices differently. Checkout only offers couriers that are on a card. Leave "to" empty for an
        "and above" row; an order heavier than the last range cannot choose that courier.
      </p>

      <div className="space-y-5">
        {cards.map((card, cardIndex) => (
          <div key={cardIndex} className="rounded-lg border border-line p-4">
            <div className="flex items-end gap-3">
              <Field label="Courier name(s), comma separated" className="max-w-md flex-1">
                <Input
                  value={card.couriers}
                  onChange={(e) => updateCard(cardIndex, { couriers: e.target.value })}
                  placeholder="e.g. J&T, NinjaVan"
                />
              </Field>
              {cards.length > 1 && (
                <Button variant="ghost" onClick={() => removeCard(cardIndex)} title="Remove this card">
                  <Trash2 className="size-4" /> Remove card
                </Button>
              )}
            </div>

            <div className="mt-4 grid gap-6 lg:grid-cols-2">
              {ZONES.map(({ key, label, hint }) => (
                <div key={key}>
                  <p className="mb-2 text-sm font-bold">
                    {label} <span className="font-normal text-muted">({hint})</span>
                  </p>
                  <div className="space-y-2">
                    {card.rows[key].map((row, index) => (
                      // Reads like the courier price card itself: [0] - [1] kg = RM [7].
                      // Below sm the same children flow into a two-line grid:
                      // [from] - [to] kg on line one, full-width price + trash on line two.
                      <div key={index} className="grid grid-cols-[1fr_auto_1fr_auto] items-center gap-2 sm:flex">
                        {/* Widths live on wrappers: inputClass carries w-full and
                            the hand-rolled cn() cannot reliably override it.
                            Three equal boxes: [from] - [to] kg = RM [price]. */}
                        <div className="min-w-0 flex-1">
                          <Input
                            type="number"
                            min={0}
                            step={0.01}
                            value={row.min_kg}
                            onChange={(e) => updateRow(cardIndex, key, index, { min_kg: e.target.value })}
                            placeholder="0"
                            className="px-1 text-center"
                          />
                        </div>
                        <span className="shrink-0 font-bold text-muted">-</span>
                        <div className="min-w-0 flex-1">
                          <Input
                            type="number"
                            min={0}
                            step={0.01}
                            value={row.max_kg}
                            onChange={(e) => updateRow(cardIndex, key, index, { max_kg: e.target.value })}
                            placeholder="above"
                            title="Leave empty for and above"
                            className="px-1 text-center"
                          />
                        </div>
                        <span className="shrink-0 text-xs font-semibold text-muted">kg</span>
                        <span className="hidden shrink-0 font-bold text-muted sm:inline">=</span>
                        <div className="relative col-span-3 min-w-0 sm:flex-1">
                          <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-xs text-muted">RM</span>
                          <Input
                            type="number"
                            min={0}
                            step={0.5}
                            value={row.price}
                            onChange={(e) => updateRow(cardIndex, key, index, { price: e.target.value })}
                            className="pl-10"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => removeRow(cardIndex, key, index)}
                          className="-m-2 justify-self-end p-2 text-muted hover:text-danger sm:m-0 sm:p-0"
                          title="Remove range"
                        >
                          <Trash2 className="size-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <Button variant="ghost" className="mt-2" onClick={() => addRow(cardIndex, key)}>
                    <Plus className="size-4" /> Add range
                  </Button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <Button variant="outline" onClick={addCard}>
          <Plus className="size-4" /> Add courier card
        </Button>
        <Button loading={saveRates.isPending} onClick={() => saveRates.mutate()}>
          Save rates
        </Button>
      </div>
    </Card>
  )
}

function toRow(rate: { min_kg: number; max_kg: number | null; price: number }): RateRow {
  return {
    min_kg: String(rate.min_kg),
    max_kg: rate.max_kg === null ? '' : String(rate.max_kg),
    price: String(rate.price),
  }
}
